import { apiClient } from './client';
import type {
  AccountPersona,
  EmailVerificationConfirmRequest,
  EmailVerificationRequest,
  LoginRequest,
  MagicLinkRedeemRequest,
  MagicLinkRequest,
  OAuthProvider,
  OAuthSignInResponse,
  PortalLoginResponse,
  UserInfoResponse,
} from './types';

// El primer canje OAuth después de un restart/deploy puede incluir cold start de Functions,
// apertura de PostgreSQL/Key Vault y descarga de JWKS del proveedor. El timeout general del
// cliente (30 s) es correcto para el resto del portal, pero corto para ese primer login.
export const OAUTH_SIGN_IN_TIMEOUT_MS = 90_000;
export const MAGIC_LINK_REQUEST_TIMEOUT_MS = 90_000;
export const EMAIL_VERIFICATION_REQUEST_TIMEOUT_MS = 90_000;

/**
 * Normaliza respuestas de autenticación de deployments que todavía serializan DTOs con
 * PascalCase. El portal nuevo usa camelCase, pero aceptar ambas formas evita que un OAuth
 * exitoso deje la UI sin JWT y recién falle en las primeras queries protegidas.
 */
function normalizeOAuthResponse(data: OAuthSignInResponse): OAuthSignInResponse {
  const source = data as unknown as Record<string, unknown>;
  const read = (camel: string, pascal: string): unknown => source[camel] ?? source[pascal];
  const rawUser = read('user', 'User');
  const userSource = (rawUser ?? {}) as Record<string, unknown>;
  const userRead = (camel: string, pascal: string): unknown =>
    userSource[camel] ?? userSource[pascal];
  const accessToken = read('accessToken', 'AccessToken');

  if (typeof accessToken !== 'string' || accessToken.trim().length === 0) {
    throw new Error('El backend no devolvió un token de sesión válido.');
  }

  return {
    accessToken: accessToken.trim(),
    tokenType: String(read('tokenType', 'TokenType') ?? 'Bearer'),
    expiresIn: Number(read('expiresIn', 'ExpiresIn') ?? 0),
    isNewUser: Boolean(read('isNewUser', 'IsNewUser')),
    user: {
      id: String(userRead('id', 'Id') ?? ''),
      email: String(userRead('email', 'Email') ?? ''),
      name: String(userRead('name', 'Name') ?? ''),
      role: userRead('role', 'Role') as UserInfoResponse['role'],
      persona: userRead('persona', 'Persona') as UserInfoResponse['persona'],
      tenantId: String(userRead('tenantId', 'TenantId') ?? ''),
      tenantName: String(userRead('tenantName', 'TenantName') ?? ''),
      isSuperAdmin: Boolean(userRead('isSuperAdmin', 'IsSuperAdmin')),
    },
  };
}

/**
 * POST /api/auth/login — el backend valida email+password y devuelve JWT del portal admin
 * (audience `afip-platform-portal`, TTL 8h). Si el email pertenece a múltiples tenants y no se
 * pasó tenantId, el endpoint devuelve 422 con error="multiple_tenants".
 */
export async function login(body: LoginRequest): Promise<PortalLoginResponse> {
  const { data } = await apiClient.post<PortalLoginResponse>('/auth/login', body);
  return data;
}

/**
 * POST /api/auth/magic/request — login passwordless. El backend genera un token de un solo uso
 * (TTL 15m) y manda el link por mail. Siempre resuelve OK (no revela si el email existe).
 */
export async function requestMagicLink(body: MagicLinkRequest): Promise<void> {
  await apiClient.post('/auth/magic/request', body, {
    timeout: MAGIC_LINK_REQUEST_TIMEOUT_MS,
  });
}

/**
 * POST /api/auth/magic/redeem — canjea el token del link y devuelve un JWT del portal (mismo
 * shape que /auth/login). 401 si el token es inválido/vencido/usado.
 */
export async function redeemMagicLink(body: MagicLinkRedeemRequest): Promise<PortalLoginResponse> {
  const { data } = await apiClient.post<PortalLoginResponse>('/auth/magic/redeem', body);
  return data;
}

/**
 * POST /api/auth/email-verification/request — solicita o reenvía el enlace. El endpoint responde
 * 202 tanto si la cuenta está pendiente como si no existe, para no revelar su estado.
 */
export async function requestEmailVerification(body: EmailVerificationRequest): Promise<void> {
  await apiClient.post('/auth/email-verification/request', body, {
    timeout: EMAIL_VERIFICATION_REQUEST_TIMEOUT_MS,
  });
}

/**
 * POST /api/auth/email-verification/confirm — consume el token de un solo uso. Un 204 confirma la
 * cuenta; la sesión se inicia después por el flujo normal de login.
 */
export async function confirmEmailVerification(body: EmailVerificationConfirmRequest): Promise<void> {
  await apiClient.post('/auth/email-verification/confirm', body);
}

/**
 * POST /api/auth/me — requiere bearer válido. Devuelve la info del user en sesión.
 * Útil para refrescar nombre/role tras un update sin requerir nuevo login.
 */
export async function getMe(): Promise<UserInfoResponse> {
  const { data } = await apiClient.post<UserInfoResponse>('/auth/me');
  return data;
}

/**
 * Slice I — POST /api/auth/oauth/{provider}. El cliente obtiene el `idToken` del SDK del IdP
 * (GIS para Google, MSAL para Microsoft) y se lo manda al backend para validar y obtener un JWT
 * del portal. Si es el primer login con SSO y el email no estaba registrado, el backend crea un
 * Tenant + TenantUser admin atómicamente y devuelve `isNewUser=true`.
 *
 * `persona` es opcional: la manda el botón de **alta** (Monotributista / Contador / Programador)
 * y se persiste en el primer login; en el botón de login no se manda (el backend usa
 * Monotributista y, si el user ya existe, se ignora de todas formas).
 *
 * Errores específicos:
 *  - 503 provider_not_configured → el deploy no tiene client id; las UI deben esconder el botón.
 *  - 401 invalid_credentials → id_token inválido (no leak-eamos detalles al cliente).
 *  - 422 multiple_tenants → email en >1 tenants; user debe usar email+password con tenantId.
 */
export async function oauthSignIn(
  provider: OAuthProvider,
  idToken: string,
  persona?: AccountPersona,
): Promise<OAuthSignInResponse> {
  const { data } = await apiClient.post<OAuthSignInResponse>(
    `/auth/oauth/${provider}`,
    { idToken, persona },
    { timeout: OAUTH_SIGN_IN_TIMEOUT_MS },
  );
  return normalizeOAuthResponse(data);
}
